import React from 'react';
import PeopleLensLogo from '../../../assets/HeaderIcons/PeopleLens_Logo.svg';

const s: Record<string, React.CSSProperties> = {
  outer: {
    background: 'rgba(217,217,217,0.4)',
    padding: '24px 16px',
    minHeight: '100vh',
  },
  container: {
    fontFamily: 'Poppins, Rubik, -apple-system, sans-serif',
    fontWeight: 400,
    maxWidth: 700,
    margin: '0 auto',
    background: '#ffffff',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  headerBlock: {
    padding: '16px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  logoImg: { height: 30, display: 'block' },
  body: { padding: '20px 28px' },
  greeting: { fontSize: 13, fontWeight: 400, color: '#171F46', lineHeight: 1.6, marginBottom: 6 },
  sectionHeader: {
    background: '#80DEEA',
    padding: '10px 14px',
    borderRadius: 4,
    margin: '24px 0 12px 0',
  },
  sectionTitle: { fontWeight: 700, fontSize: 14, color: '#171F46', margin: 0 },
  ul: { paddingLeft: 20, paddingRight: 14, margin: '8px 0' },
  li: { marginBottom: 10, fontSize: 13, fontWeight: 400, lineHeight: 1.6, color: '#171F46' },
  table: { width: '100%', borderCollapse: 'collapse' as const, margin: '12px 0', fontSize: 12 },
  th: {
    background: 'rgba(232,236,239,0.71)',
    color: '#171F46',
    padding: '10px 8px',
    textAlign: 'left' as const,
    fontWeight: 700,
  },
  td: { padding: '10px 8px', borderBottom: '1px solid #e5e7eb', color: '#333', fontWeight: 400, verticalAlign: 'top' as const },
  tdAlt: { padding: '10px 8px', borderBottom: '1px solid #e5e7eb', color: '#333', fontWeight: 400, background: '#f9fafb', verticalAlign: 'top' as const },
  tdBold: { padding: '10px 8px', borderBottom: '1px solid #e5e7eb', color: '#171F46', fontWeight: 700, background: '#ffffff', verticalAlign: 'top' as const },
  tdTotal: { padding: '10px 8px', borderBottom: '1px solid #e5e7eb', color: '#171F46', fontWeight: 700, background: '#f3f4f6', verticalAlign: 'top' as const },
  note: { fontSize: 11, fontWeight: 400, lineHeight: 1.6, color: '#6b7280', fontStyle: 'italic', margin: '4px 0 16px 0' },
  signature: { fontSize: 13, fontWeight: 400, color: '#171F46', marginTop: 20, lineHeight: 1.7 },
  loginBtn: {
    display: 'inline-block', background: '#0B69FF', color: '#fff',
    padding: '8px 20px', borderRadius: 22, fontSize: 13, fontWeight: 700,
    textDecoration: 'none', marginRight: 6, marginTop: 4,
    whiteSpace: 'nowrap' as const,
  },
  resourceBtn: {
    display: 'inline-block', background: '#facc15', color: '#171F46',
    padding: '8px 20px', borderRadius: 22, fontSize: 13, fontWeight: 700,
    textDecoration: 'none', marginRight: 6, marginTop: 4,
    whiteSpace: 'nowrap' as const,
  },
};

interface QuarterlyReportViewProps { data: any; }

const H = ({ html }: { html: string }) => (
  <span dangerouslySetInnerHTML={{ __html: html || '' }} />
);

const SectionHeader = ({ num, title }: { num?: number; title: string }) => (
  <div style={s.sectionHeader}>
    <span style={s.sectionTitle}>{num != null ? `${num}. ` : ''}<H html={title} /></span>
  </div>
);

const FmtCell = ({ cur, prev }: { cur: any; prev?: any }) => {
  if (cur == null && prev == null) return <>—</>;
  const curStr = cur != null ? String(cur) : '—';
  if (prev == null) return <span dangerouslySetInnerHTML={{ __html: curStr }} />;
  return <span dangerouslySetInnerHTML={{ __html: `${curStr} (${String(prev)})` }} />;
};

const QuarterlyReportView: React.FC<QuarterlyReportViewProps> = ({ data }) => {
  if (!data) return null;
  const {
    reportMetadata, executiveSummary,
    section_1, section_2, section_3, section_4, section_5,
    section_6, section_7, section_8, section_9,
  } = data;

  const curLabel = section_5?.aes?.[0]?.current?.quarter || 'Current';
  const prevLabel = section_5?.aes?.[0]?.previous?.quarter || 'Previous';

  return (
    <div style={s.outer}>
      <div style={s.container}>

        {/* Header */}
        <div style={s.headerBlock}>
          <img src={PeopleLensLogo} style={s.logoImg} alt="PeopleLens" />
        </div>

        <div style={s.body}>
          {/* Greeting */}
          {reportMetadata?.greeting && <p style={s.greeting}><H html={reportMetadata.greeting} /></p>}
          {reportMetadata?.introLine1 && <p style={s.greeting}><H html={reportMetadata.introLine1} /></p>}
          {reportMetadata?.introLine2 && <p style={{ ...s.greeting, marginBottom: 0 }}><H html={reportMetadata.introLine2} /></p>}

          {/* Executive Summary */}
          {executiveSummary && (
            <>
              <SectionHeader title={executiveSummary.title || 'Executive Summary'} />
              <ul style={s.ul}>
                {[
                  executiveSummary.closedWon,
                  executiveSummary.winningPattern,
                  executiveSummary.enterpriseAccelerator,
                  executiveSummary.primaryFriction,
                  executiveSummary.keyGap,
                  executiveSummary.q2Focus,
                ].filter(Boolean).map((item: string, i: number) => (
                  <li key={i} style={s.li}><H html={item} /></li>
                ))}
              </ul>
            </>
          )}

          {/* Section 1: What's Driving Revenue */}
          {section_1 && (
            <>
              <SectionHeader num={1} title={section_1.sectionTitle} />
              <ul style={s.ul}>
                {section_1.behaviors?.map((b: any, i: number) => (
                  <li key={i} style={s.li}><H html={b.title} />{b.description ? ': ' : ''}<H html={b.description} /></li>
                ))}
              </ul>
            </>
          )}

          {/* Section 2: Key Deal Risks */}
          {section_2 && (
            <>
              <SectionHeader num={2} title={section_2.sectionTitle} />
              <ul style={s.ul}>
                {section_2.risks?.map((r: any, i: number) => (
                  <li key={i} style={s.li}><H html={r.title} />{r.description ? ': ' : ''}<H html={r.description} /></li>
                ))}
              </ul>
            </>
          )}

          {/* Section 3: AE Performance Insights */}
          {section_3 && (
            <>
              <SectionHeader num={3} title={section_3.sectionTitle} />
              <ul style={s.ul}>
                {section_3.aes?.map((ae: any, i: number) => (
                  <li key={i} style={s.li}>
                    <H html={ae.ae} />
                    {ae.role ? <span style={{ color: '#6b7280', fontSize: 12 }}> ({ae.role})</span> : null}
                    {ae.insight ? ': ' : ''}<H html={ae.insight} />
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Section 4: Closed Won Deal Summary */}
          {section_4 && (
            <>
              <SectionHeader num={4} title={section_4.sectionTitle} />
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Account</th>
                      <th style={s.th}>AE</th>
                      <th style={s.th}>Booking ($)</th>
                      <th style={s.th}>Closed Won Date</th>
                      <th style={s.th}>Strategic Key to the Win</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section_4.deals?.map((d: any, i: number) => (
                      <tr key={i}>
                        <td style={{ ...s.tdBold, background: i % 2 === 0 ? '#ffffff' : '#f9fafb' }}><H html={d.account} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><H html={d.ae || ''} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><H html={String(d.booking ?? '')} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><H html={String(d.closedWonDate ?? '')} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><H html={d.strategicKey || '—'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Section 5: Funnel & Activity by AE — flat table */}
          {section_5 && (
            <>
              <SectionHeader num={5} title={section_5.sectionTitle} />
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>AE</th>
                      <th style={s.th}>Total Deals*</th>
                      <th style={s.th}>Closed Won</th>
                      <th style={s.th}>Closed Lost</th>
                      <th style={s.th}>Total Calls</th>
                      <th style={s.th}>Total Bookings</th>
                      <th style={s.th}>Win Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section_5.aes?.map((ae: any, i: number) => (
                      <tr key={i}>
                        <td style={{ ...s.tdBold, background: i % 2 === 0 ? '#ffffff' : '#f9fafb' }}><H html={ae.ae} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><FmtCell cur={ae.current?.totalDeals} prev={ae.previous?.totalDeals} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><FmtCell cur={ae.current?.closedWon} prev={ae.previous?.closedWon} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><FmtCell cur={ae.current?.closedLost} prev={ae.previous?.closedLost} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><FmtCell cur={ae.current?.totalCalls} prev={ae.previous?.totalCalls} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><FmtCell cur={ae.current?.totalBookings} prev={ae.previous?.totalBookings} /></td>
                        <td style={i % 2 === 0 ? s.td : s.tdAlt}><FmtCell cur={ae.current?.winRate} prev={ae.previous?.winRate} /></td>
                      </tr>
                    ))}
                    {section_5.totals && (
                      <tr>
                        <td style={s.tdTotal}>Total</td>
                        <td style={s.tdTotal}><FmtCell cur={section_5.totals.totalDeals} prev={section_5.totals.prevTotalDeals} /></td>
                        <td style={s.tdTotal}><FmtCell cur={section_5.totals.closedWon} prev={section_5.totals.prevClosedWon} /></td>
                        <td style={s.tdTotal}><FmtCell cur={section_5.totals.closedLost} prev={section_5.totals.prevClosedLost} /></td>
                        <td style={s.tdTotal}><FmtCell cur={section_5.totals.totalCalls} prev={section_5.totals.prevTotalCalls} /></td>
                        <td style={s.tdTotal}><FmtCell cur={section_5.totals.totalBookings} prev={section_5.totals.prevTotalBookings} /></td>
                        <td style={s.tdTotal}><FmtCell cur={section_5.totals.winRate} prev={section_5.totals.prevWinRate} /></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p style={s.note}>* {curLabel} ({prevLabel}). Total deals = unique deals associated with calls during the quarter.</p>
            </>
          )}

          {/* Section 6: Closed Lost Analysis */}
          {section_6 && (
            <>
              <SectionHeader num={6} title={section_6.sectionTitle} />
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Closed Lost Reason</th>
                      <th style={s.th}># of Deals</th>
                      <th style={s.th}>Total Value Lost</th>
                      <th style={s.th}>% of Closed Lost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section_6.reasons?.map((r: any, i: number) => {
                      const isTotal = String(r.reason).toLowerCase() === 'total';
                      const cellStyle = isTotal ? s.tdTotal : (i % 2 === 0 ? s.td : s.tdAlt);
                      return (
                        <tr key={i}>
                          <td style={cellStyle}><H html={r.reason} /></td>
                          <td style={cellStyle}><H html={String(r.numberOfDeals ?? '')} /></td>
                          <td style={cellStyle}><H html={String(r.totalValueLost ?? '')} /></td>
                          <td style={cellStyle}><H html={String(r.percentageOfClosedLost ?? '')} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {section_6.insights?.length > 0 && (
                <ul style={s.ul}>
                  {section_6.insights.map((insight: string, i: number) => (
                    <li key={i} style={s.li}><H html={insight} /></li>
                  ))}
                </ul>
              )}
            </>
          )}

          {/* Section 7: Deal Age Analysis */}
          {section_7 && (
            <>
              <SectionHeader num={7} title={section_7.sectionTitle} />
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Deal Age Cohort</th>
                      <th style={s.th}># of Deals</th>
                      <th style={s.th}>Total Pipeline ($)</th>
                      <th style={s.th}>% of Pipeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section_7.cohorts?.map((c: any, i: number) => {
                      const isTotal = String(c.dealAgeCohort).toLowerCase() === 'total';
                      const cellStyle = isTotal ? s.tdTotal : (i % 2 === 0 ? s.td : s.tdAlt);
                      return (
                        <tr key={i}>
                          <td style={{ ...cellStyle, fontWeight: 700 }}><H html={c.dealAgeCohort} /></td>
                          <td style={cellStyle}><H html={String(c.numberOfDeals)} /></td>
                          <td style={cellStyle}><H html={String(c.totalPipeline)} /></td>
                          <td style={cellStyle}><H html={String(c.percentageOfPipeline)} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {section_7.notes?.length > 0 && (
                <p style={{ ...s.note, margin: '8px 0 12px 0' }}>
                  Notes: {section_7.notes.map((note: string, i: number) => (
                    <span key={i}><H html={note} />{i < section_7.notes.length - 1 ? ' ' : ''}</span>
                  ))}
                </p>
              )}
              {section_7.insights?.length > 0 && (
                <ul style={s.ul}>
                  {section_7.insights.map((insight: string, i: number) => (
                    <li key={i} style={s.li}><H html={insight} /></li>
                  ))}
                </ul>
              )}
            </>
          )}

          {/* Section 8: Q2 Priorities & Actions */}
          {section_8 && (
            <>
              <SectionHeader num={8} title={section_8.sectionTitle} />
              <ul style={s.ul}>
                {section_8.priorities?.map((p: any, i: number) => (
                  <li key={i} style={s.li}><H html={p.title} />{p.description ? ': ' : ''}<H html={p.description} /></li>
                ))}
              </ul>
            </>
          )}

          {/* Section 9: Bottom Line */}
          {section_9 && (
            <>
              <SectionHeader num={9} title={section_9.sectionTitle} />
              <ul style={s.ul}>
                {section_9.points?.map((p: string, i: number) => (
                  <li key={i} style={s.li}><H html={p} /></li>
                ))}
              </ul>
              {section_9.signature && (
                <p style={s.signature}>
                  {section_9.signature.closing},<br />{section_9.signature.signedBy}
                </p>
              )}
            </>
          )}

          {/* Footer */}
          <div style={{ marginTop: 16 }}>
            <a href="https://regology-peoplelens.web.app/" target="_blank" rel="noopener noreferrer" style={s.loginBtn}>Login</a>
            <a href="https://peoplelens.ai/resources/DiscoveryFramework.pdf" target="_blank" rel="noopener noreferrer" style={s.resourceBtn}>View Resources</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuarterlyReportView;
